import React from "react"
import styled, { css, keyframes } from "styled-components"
import { fontWeight, responsive } from "buoy-theme"
import {
  Card,
  Checklist,
  Tag,
  HeadingThree,
  Paragraph,
  LinkButton,
  Ul,
  RoundButton,
} from "buoy-core"
import { InfoList } from "./components/InfoList"
import { clickLinkTracker } from "../../../completion/actions"
import Source from "../../Source"

const moveUp = keyframes`
    0% {

    }
    100% {
        transform: translateY(0);
        opacity: 1;
    }
`

const RoundLinkButton = styled(LinkButton)`
  line-height: 1.25;
  border-radius: 100px;
  padding: ${props => (props.large ? "16px 48px" : "13px 48px")};
`

const CardContainer = styled(Card)`
  margin: 12px 0;
  overflow: visible;
  width: 100%;

  ${props =>
    props.fullBorder
      ? css`
          box-shadow: none;
          border: solid 1px
            ${props => (props.borderColor ? props.borderColor : "#b9b9b9")};
        `
      : ""};
  ${props =>
    props.animatable
      ? css`
          opacity: 0;
          transform: translateY(100px);
        `
      : ""};

  ${props =>
    props.animate
      ? css`
          animation: ${moveUp} 0.4s ease forwards;
        `
      : ""};

  ${props =>
    props.diagnosisCard
      ? css`
          width: auto;
          ${responsive(
            "margin",
            "0 -32px 40px",
            "0 -32px 40px",
            "0 0 24px",
            "0 0 24px",
            "0 0 24px"
          )};

          ${responsive(
            "box-sizing",
            "unset",
            "unset",
            "border-box",
            "border-box",
            "border-box"
          )};
        `
      : ""}

  &:nth-child(even) {
    animation-delay: 0.1s;
  }
`

const CardHeadline = styled(HeadingThree)`
  font-size: 30px;
  line-height: 34px;
  letter-spacing: -0.2px;
  font-weight: ${fontWeight.light};
  margin-top: 0;
  margin-bottom: 16px;
`

const InlineCardHeadline = styled(HeadingThree)`
  font-size: 30px;
  line-height: 34px;
  letter-spacing: -0.2px;
  font-weight: ${fontWeight.light};
  margin-top: 0;
  margin-bottom: 0;
  margin-right: 8px;
`

const CardText = styled(Paragraph)`
  padding-bottom: 16px;
  margin-bottom: 6px;
`

const CardContent = styled.div`
  ${responsive("padding", "24px", "24px", "24px", "24px", "24px 16px")};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`

const ChecklistWrapper = styled.div`
  padding: 20px 0 16px 0;
  border-top: solid 1px #d9d9d9;

  ul > li {
    margin-bottom: 6px;
    padding-left: 4px;
  }
`

const TitleHeader = styled.div`
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`

const TagHeader = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const BrandImage = styled.img`
  max-height: 32px;
  max-width: 110px;
`

// TODO: Are some of these properties (font-smoothing, letter-spacing,
// padding) be added to the common component itself?
const CardTag = styled(Tag)`
  margin-left: 10px;
  letter-spacing: 0.9px;
  -webkit-font-smoothing: antialiased;
  padding: 3px 15px;

  &:first-child {
    margin-left: 0;
  }
`

const ChecklistUl = styled(Ul)`
  list-style: none;
  padding-left: 21px;
  margin: 20px 0;
`

const trackAction = (token, actionType, href) => {
  Source.trackUserRecommendationAction(token, actionType)
  clickLinkTracker(href)
  window.open(href, "_blank")
}

const trackIntellivisit = (token, initAction) => {
  Source.trackUserRecommendationAction(token, "move_to_handoff")
  initAction()
}

const trackPaymentFlow = (token, initAction, rec, diffToken) => {
  Source.trackUserRecommendationAction(token, "move_to_purchase")
  initAction(rec, diffToken)
}

const displayModal = (showModal, trackerEvent = null) => {
  showModal(true)
  if (trackerEvent) trackerEvent()
}

const RecommendationCard = ({
  token,
  children,
  heroImg,
  heroText,
  borderColor,
  tags,
  image,
  headline,
  infoList,
  description,
  highlights,
  ctaText,
  ctaUrl,
  animatable,
  animate,
  fullBorder,
  float,
  requirePayment,
  requireProfile,
  rec,
  initPayment,
  initIntellivisit,
  diffToken,
  showModal,
  diagnosisCard,
  trackerEvent,
  component,
}) => {
  return (
    <CardContainer
      heroImg={heroImg}
      heroText={heroText}
      borderColor={borderColor}
      animatable={animatable}
      animate={animate}
      fullBorder={fullBorder}
      topBorder={fullBorder ? null : borderColor}
      float={float}
      diagnosisCard={diagnosisCard}
    >
      <CardContent>
        <div>
          {tags ? (
            <div>
              <TagHeader>
                {tags.map(tag => (
                  <CardTag key={tag.order}>{tag.tag}</CardTag>
                ))}
                {image && <BrandImage src={image} />}
              </TagHeader>
              <CardHeadline>{headline}</CardHeadline>
            </div>
          ) : (
            <TitleHeader>
              <InlineCardHeadline>{headline}</InlineCardHeadline>
              {image && <BrandImage src={image} />}
            </TitleHeader>
          )}
          {infoList && Object.keys(infoList).length !== 0 && (
            <ChecklistUl>
              <InfoList infoList={infoList} />
            </ChecklistUl>
          )}
          {description && <CardText>{description}</CardText>}
          {highlights && highlights.length !== 0 && (
            <ChecklistWrapper>
              <Checklist
                color="neutralDark"
                items={highlights.map(highlight => highlight.highlight)}
              />
            </ChecklistWrapper>
          )}
        </div>
        {component}
        {ctaText &&
          (requirePayment === "True" ||
          requireProfile === "True" ||
          showModal ? (
            <div>
              <RoundButton
                fullWidth
                onClick={() => {
                  if (requirePayment === "True")
                    trackPaymentFlow(token, initPayment, rec, diffToken)
                  else if (showModal) displayModal(showModal, trackerEvent)
                  else trackIntellivisit(token, initIntellivisit)
                }}
                backgroundColor="neutralLightest"
                hoverBackgroundColor="primary"
                textColor="primary"
                hoverTextColor="neutralLightest"
              >
                {ctaText}
              </RoundButton>
            </div>
          ) : (
            <div>
              <RoundLinkButton
                fullWidth
                onClick={() => trackAction(token, "visit_site", ctaUrl)}
                rel="noopener"
                backgroundColor="neutralLightest"
                hoverBackgroundColor="primary"
                textColor="primary"
                hoverTextColor="neutralLightest"
              >
                {ctaText}
              </RoundLinkButton>
            </div>
          ))}
      </CardContent>
    </CardContainer>
  )
}

export { RecommendationCard as default }



// WEBPACK FOOTER //
// ./assets/common/components/RecommendationCard/index.js